export type WeightInputUnit = 'g' | 'kg';

export const WEIGHT_EPS = 0.0005;

export function roundKg(kg: number): number {
  return Math.round(kg * 1000) / 1000;
}

export function hasPositiveWeight(weightKg: unknown): weightKg is number {
  return typeof weightKg === 'number' && Number.isFinite(weightKg) && weightKg > 0;
}

export function isWeightLine(item: { weightKg?: number | null }): boolean {
  return hasPositiveWeight(item.weightKg);
}

export function toKg(value: string | number, unit: WeightInputUnit): number | null {
  const raw =
    typeof value === 'number'
      ? value
      : parseFloat(String(value).trim().replace(',', '.'));
  if (!Number.isFinite(raw) || raw <= 0) return null;
  const kg = unit === 'g' ? raw / 1000 : raw;
  if (!Number.isFinite(kg) || kg <= 0) return null;
  return roundKg(kg);
}

export function calculateWeightLinePrice(pricePerKg: number, kg: number): number {
  if (!Number.isFinite(pricePerKg) || !Number.isFinite(kg) || kg <= 0) return 0;
  return Math.round(pricePerKg * kg * 100) / 100;
}

export function getPricePerKg(params: {
  unitPrice: number;
  weightKg: number;
  catalogPrice?: number | null;
}): number {
  const { unitPrice, weightKg, catalogPrice } = params;
  if (typeof catalogPrice === 'number' && catalogPrice > 0) return catalogPrice;
  if (!hasPositiveWeight(weightKg) || !Number.isFinite(unitPrice)) return 0;
  return unitPrice / weightKg;
}

/** Display helper: prefer grams under 1 kg. */
export function formatWeight(kg: number): string {
  if (!Number.isFinite(kg) || kg <= 0) return '0 g';
  if (kg < 1) {
    const grams = Math.round(kg * 1000);
    return `${grams} g`;
  }
  const trimmed = Number(kg.toFixed(3));
  return `${trimmed} kg`;
}

export function formatWeightBreakdown(kg: number): string {
  if (!Number.isFinite(kg) || kg <= 0) return '0 g';
  const totalGrams = Math.round(kg * 1000);
  const wholeKg = Math.floor(totalGrams / 1000);
  const grams = totalGrams % 1000;
  if (wholeKg === 0) return `${grams} g`;
  if (grams === 0) return `${wholeKg} kg`;
  return `${wholeKg} kg ${grams} g`;
}

export function formatQtyDisplay(amount: number, soldByWeight: boolean): string {
  return soldByWeight ? formatWeight(amount) : String(amount);
}

/** Stock delta for a tab line: weight in kg when present, else unit quantity. */
export function getStockUnits(item: { quantity: number; weightKg?: number | null }): number {
  if (hasPositiveWeight(item.weightKg)) {
    return item.weightKg;
  }
  return item.quantity;
}

/** Parse UI weight input; 0 is allowed (e.g. remove line). Invalid / empty → null. */
export function parseWeightInput(
  value: string | number,
  unit: WeightInputUnit
): number | null {
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value < 0) return null;
    if (value === 0) return 0;
    return toKg(value, unit);
  }
  const trimmed = String(value).trim().replace(',', '.');
  if (trimmed === '') return null;
  const raw = parseFloat(trimmed);
  if (!Number.isFinite(raw) || raw < 0) return null;
  if (raw === 0) return 0;
  return toKg(raw, unit);
}

/** Prefill editor: grams under 1 kg, otherwise kg. */
export function initialWeightInput(kg: number): { value: string; unit: WeightInputUnit } {
  if (!Number.isFinite(kg) || kg <= 0) {
    return { value: '', unit: 'g' };
  }
  if (kg < 1) {
    return { value: String(Math.round(kg * 1000)), unit: 'g' };
  }
  return { value: String(Number(kg.toFixed(3))), unit: 'kg' };
}

/** Pure split of a weight line for tab moves (prices rounded like calculateWeightLinePrice). */
export function splitWeightLine(params: {
  unitPrice: number;
  sourceWeightKg: number;
  weightKgToMove: number;
  catalogPrice?: number | null;
}): {
  isFullMove: boolean;
  movedWeightKg: number;
  movedUnitPrice: number;
  remainingWeightKg: number;
  remainingUnitPrice: number;
} {
  const { unitPrice, sourceWeightKg, weightKgToMove, catalogPrice } = params;
  const isFullMove = weightKgToMove >= sourceWeightKg - WEIGHT_EPS;
  if (isFullMove) {
    return {
      isFullMove: true,
      movedWeightKg: sourceWeightKg,
      movedUnitPrice: unitPrice,
      remainingWeightKg: 0,
      remainingUnitPrice: 0,
    };
  }

  const pricePerKg = getPricePerKg({ unitPrice, weightKg: sourceWeightKg, catalogPrice });
  const remainingWeightKg = roundKg(sourceWeightKg - weightKgToMove);
  return {
    isFullMove: false,
    movedWeightKg: weightKgToMove,
    movedUnitPrice: calculateWeightLinePrice(pricePerKg, weightKgToMove),
    remainingWeightKg,
    remainingUnitPrice: calculateWeightLinePrice(pricePerKg, remainingWeightKg),
  };
}

/** Snapshot entry is a partial weight split (not a full row move). */
export function isWeightPartialMove(entry: {
  weightKg?: number | null;
  remainingWeightKg?: number | null;
  remainingUnitPrice?: number | null;
}): boolean {
  return (
    hasPositiveWeight(entry.weightKg) &&
    typeof entry.remainingWeightKg === 'number' &&
    Number.isFinite(entry.remainingWeightKg) &&
    typeof entry.remainingUnitPrice === 'number' &&
    Number.isFinite(entry.remainingUnitPrice)
  );
}
